import { Alert, DatePicker, Input, Modal } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useCreateLedgerExpense } from "../../api/api";
import { useCurrencyContext } from "../../providers/currency.provider";

export default function CreateLedgerExpenseDialog({
  open,
  onCancel,
  onSuccess,
  ledgerId,
  groupId,
}: {
  groupId?: string;
  ledgerId: string;
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const { mutateAsync, error, isPending } = useCreateLedgerExpense(
    groupId,
    ledgerId,
  );

  const { currency } = useCurrencyContext();

  const formik = useFormik({
    initialValues: {
      description: "",
      transactionDate: "",
      amount: 0,
    },
    validationSchema: Yup.object({
      description: Yup.string().required("Please enter description"),
      transactionDate: Yup.string().required("Please select date"),
      amount: Yup.number().required("Please enter number"),
    }),
    onSubmit: async (values) => {
      mutateAsync({
        amount: values.amount ? +values.amount : 0,
        expenseDate: values.transactionDate,
        comments: values.description,
        type: "ledger",
      }).then(() => {
        onSuccess();
      });
    },
  });

  return (
    <form className="space-y-6" onSubmit={formik.handleSubmit}>
      <Modal
        title="Add Transaction"
        closable={{ "aria-label": "Custom Close Button" }}
        open={open}
        onOk={formik.submitForm}
        onCancel={onCancel}
        okText="Create"
        okButtonProps={{ loading: isPending }}
      >
        <div className="flex flex-col my-10">
          {error && <Alert type="error" title="Whoops! Please try again" />}
          {/* Savings Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Expense Name *
            </label>
            <Input
              placeholder="e.g., Emergency Fund, Vacation, New Car"
              {...formik.getFieldProps("description")}
              required
              status={!!formik.errors.description ? "error" : undefined}
            />
          </div>

          {/* Date */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Transaction Date *
            </label>
            <DatePicker
              onChange={(date) =>
                formik.setFieldValue(
                  "transactionDate",
                  date?.format("YYYY-MM-DD"),
                )
              }
            />

            <p className="text-xs text-muted-foreground mt-1">
              When do you want to record this transaction?
            </p>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                prefix={currency}
                type={"number"}
                placeholder="e.g., 100.00"
                {...formik.getFieldProps("amount")}
                status={!!formik.errors.amount ? "error" : undefined}
                required
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Enter the amount
            </p>
          </div>
        </div>
      </Modal>
    </form>
  );
}
